import { useCallback, useContext, useState } from "react";

// Types
import { ApiMenuCartItem, CartItemWithUUID, DishStyle, RestaurantDetails } from "@swiggy-private/cart-types";

// Contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";

// Utils
import { getBridgeSdk } from "@swiggy-private/checkout-core";

// Helpers
import {
    checkUpdatedItemAlreadyExistInItemsList,
    getExistingCartItem,
    getItemsListForCartUpdate,
    getUniqueIdForCartItem,
} from "@cart-client/helpers/CartItemRenderer";
import { isEmpty } from "@swiggy-private/js-utils";

interface UseCartItemActionsProps {
    cartItemsList: CartItemWithUUID[];
    isGroupOrderFlow?: boolean;
    updateGroupCart?: (cartItems: ApiMenuCartItem[]) => void;
    source?: string;
    restaurantDetails?: RestaurantDetails;
    dishStyle?: DishStyle;
}

export const useCartItemActions = ({
    isGroupOrderFlow = false,
    updateGroupCart,
    cartItemsList,
    restaurantDetails,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    source = "cart-item-renderer",
    dishStyle,
}: UseCartItemActionsProps) => {
    const { doUpdateCart, updateCartInProgress } = useContext(UpdateCartContext);
    const {
        cartState: { localCart },
    } = useContext(CartContext);
    const [updatingItem, setUpdatingItem] = useState<CartItemWithUUID | null>(null);

    /**
     * Updates the quantity of an item in the cart
     * Handles both regular cart and group order flows
     * Clears cart if all items are removed
     */
    const onItemUpdate = useCallback(
        async (item: CartItemWithUUID) => {
            // Filter out the item being updated from the cart list
            let updatedCartItemsList: CartItemWithUUID[] = [];
            updatedCartItemsList = cartItemsList.filter(cartItem => cartItem.unId !== item.unId);

            // Add the item back if its quantity is greater than 0
            if (item.quantity > 0) {
                updatedCartItemsList.push(item);
            }

            const sortedItemsList = getItemsListForCartUpdate(updatedCartItemsList);

            // Handle group order flow separately
            if (isGroupOrderFlow) {
                updateGroupCart?.(sortedItemsList);
                return;
            }

            // Clear local cart if all items are removed
            if (!sortedItemsList || sortedItemsList.length < 1) {
                getBridgeSdk().clearLocalCart({
                    tenant: {
                        marketplaceBusinessline: "Regular",
                        marketplaceCategory: "Food",
                        marketplaceId: 1,
                    },
                });
            }

            // Update cart with new item list
            if (!updateCartInProgress) {
                await doUpdateCart({ ...localCart, cartItems: sortedItemsList });
                setUpdatingItem(null);
            }
        },
        [cartItemsList, isGroupOrderFlow, updateGroupCart, updateCartInProgress, source, doUpdateCart, localCart],
    );

    /**
     * Handles item customization including addons and variants
     * Can either update existing item or add a new customized item
     */
    const onItemCustomisation = useCallback(
        async (item: CartItemWithUUID, addNewItem = false): Promise<void> => {
            // open customisation screen and get the customisation details
            const data: {
                [key: string]: object | string | number | boolean;
            } = {};

            // In some cases the base price is zero, example when you have variants of a item
            // in such cases we need to consider variants price.
            const basePrice = item.base_price > 0 ? item.base_price : Number(item.variants_price || 0);
            /* eslint-disable @typescript-eslint/naming-convention */
            data["menuItem"] = { ...item, base_price: basePrice };
            data["restaurantDetails"] = {
                id: restaurantDetails?.id,
                name: restaurantDetails?.name,
                dish_style: dishStyle || {},
            };
            data["addNewItem"] = addNewItem;

            getBridgeSdk()
                .customiseCartItem({
                    tenant: {
                        marketplaceBusinessline: "Regular",
                        marketplaceCategory: "Food",
                        marketplaceId: 1,
                    },
                    data,
                })
                .then(async response => {
                    // If user din't selected any addons
                    if (!response) {
                        setUpdatingItem(null);
                        return;
                    }

                    // prepeare final items list
                    let updatedCartItemsList: CartItemWithUUID[] = [];

                    // if user is adding new customised item
                    if (addNewItem) {
                        // create new item with updated variants and addons
                        const newItem: CartItemWithUUID = {
                            ...item,
                            addons: response.addons,
                            variants: response.variants,
                            quantity: 1,
                            unId: getUniqueIdForCartItem(item.menu_item_id, cartItemsList.length + 1),
                            itemPosition: cartItemsList.length + 1,
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            customer_request_messages: [],
                        };

                        // check if new item variation already exist in cart item list
                        if (checkUpdatedItemAlreadyExistInItemsList(cartItemsList, newItem)) {
                            // update the existing item quantity with new item quantity
                            const sameCartItem: CartItemWithUUID = getExistingCartItem(
                                cartItemsList,
                                newItem,
                            ) as CartItemWithUUID;
                            sameCartItem.quantity += newItem.quantity;

                            // get other cart items from updating one
                            updatedCartItemsList = cartItemsList.filter(
                                cartItem => cartItem.unId !== sameCartItem.unId,
                            );

                            // push the existing item with increased quantity
                            updatedCartItemsList.push(sameCartItem);
                        } else {
                            // push existing cart item list along with new item directly
                            updatedCartItemsList.push(...cartItemsList, newItem);
                        }
                    } else {
                        // append to updating item
                        const updatingItem = {
                            ...item,
                            addons: response.addons,
                            variants: response.variants,
                        };
                        const otherCartItems = cartItemsList.filter(cartItem => cartItem.unId !== updatingItem.unId);

                        // check if updated item already exist in other items list
                        if (checkUpdatedItemAlreadyExistInItemsList(otherCartItems, updatingItem)) {
                            // update the existing item quantity with new item
                            const sameCartItem: CartItemWithUUID = getExistingCartItem(
                                otherCartItems,
                                updatingItem,
                            ) as CartItemWithUUID;
                            sameCartItem.quantity += updatingItem.quantity;

                            // get other cart items from updating and existing one
                            updatedCartItemsList = cartItemsList.filter(
                                cartItem => [updatingItem.unId, sameCartItem.unId].indexOf(cartItem.unId) === -1,
                            );

                            // push the existing item with increased quantity
                            updatedCartItemsList.push(sameCartItem);
                        } else {
                            // get other cart items from updating
                            updatedCartItemsList = cartItemsList.filter(
                                cartItem => cartItem.unId !== updatingItem.unId,
                            );
                            updatedCartItemsList.push(updatingItem);
                        }
                    }

                    // perform the update cart operation
                    if (!updateCartInProgress) {
                        await doUpdateCart({
                            ...localCart,
                            cartItems: getItemsListForCartUpdate(updatedCartItemsList),
                        });
                        setUpdatingItem(null);
                    }
                })
                .catch(() => void 0);
        },
        [restaurantDetails, updateCartInProgress, cartItemsList, doUpdateCart, localCart, dishStyle],
    );

    /**
     * Adds a new item to the cart
     * Creates a deep copy of current cart items to avoid mutation
     * Currently used in cross sell carousel only
     */
    const addItem = useCallback(
        async (item: CartItemWithUUID) => {
            if (!updateCartInProgress) {
                try {
                    const cartItemsInitial = JSON.parse(JSON.stringify(localCart?.cartItems));

                    // Ensure item has a valid quantity (at least 1)
                    const validatedItem = {
                        ...item,
                        quantity: isEmpty(item?.quantity) || item.quantity <= 0 ? 1 : item.quantity,
                    };

                    cartItemsInitial.push(validatedItem);

                    const updatedItem = { ...localCart, cartItems: cartItemsInitial };

                    await doUpdateCart({
                        ...updatedItem,
                    });
                } catch (e) {
                    console.error(e);
                }
            }
        },
        [doUpdateCart, updateCartInProgress, localCart],
    );

    return {
        onItemUpdate,
        onItemCustomisation,
        addItem,
        updateCartInProgress,
        updatingItem,
        setUpdatingItem,
    };
};
